/**
 * analytics-gate.js
 *
 * Suppresses Shopify + third-party analytics until a visitor looks human.
 * Purpose: keep bot traffic out of analytics. It does NOT block anyone.
 *
 * Design rules:
 *   - Fail OPEN. Any error, any doubt -> release. A missed bot is cheaper
 *     than a suppressed real customer.
 *   - Never touch page rendering, checkout, or app functionality.
 *   - Only defer scripts on the SUPPRESS list. Everything else runs normally.
 *
 * Install: theme app extension, or a <script> in theme.liquid inside <head>,
 * placed BEFORE Shopify's analytics tags so the observer sees them.
 */
(function () {
  'use strict';

  // ---- config ---------------------------------------------------------

  var CONFIG = {
    // Release without interaction after this long if signals are clean.
    // Keeps bounce traffic in your numbers.
    dwellReleaseMs: 4000,

    // Hard ceiling. Release no matter what. Safety valve.
    maxHoldMs: 15000,

    // Script src / inline-content markers to defer.
    suppress: [
      'trekkie.storefront',
      'ShopifyAnalytics.lib',
      'web-pixels-manager',
      'shopify.webPixelsManager',
      'monorail-edge',
      'googletagmanager.com/gtm.js',
      'googletagmanager.com/gtag',
      'google-analytics.com/analytics',
      'connect.facebook.net'
    ],

    // Endpoints to hold. Beacons/fetches matching these are queued.
    suppressNet: [
      'monorail-edge.shopifysvc.com',
      'google-analytics.com',
      'analytics.google.com',
      'facebook.com/tr'
    ],

    debug: false
  };

  // Never interfere in the theme editor or in checkout.
  if (window.Shopify && window.Shopify.designMode) return;
  if (location.pathname.indexOf('/checkouts/') === 0) return;
  if (location.pathname.indexOf('/gift_cards/') === 0) return;

  function log() {
    if (CONFIG.debug) console.log.apply(console, ['[gate]'].concat([].slice.call(arguments)));
  }

  // ---- signal collection ----------------------------------------------

  function collectSignals() {
    var s = { hard: [], soft: [] };

    try {
      if (navigator.webdriver === true) s.hard.push('webdriver');

      if (window._playwright || window.__playwright || window.__pwInitScripts) {
        s.hard.push('playwright');
      }
      if (window.__nightmare || window.callPhantom || window._phantom) {
        s.hard.push('phantom');
      }
      // Puppeteer/CDP artifacts
      if (window.__puppeteer_evaluation_script__) s.hard.push('puppeteer');

      // Headless brand in Client Hints
      var brands = (navigator.userAgentData && navigator.userAgentData.brands) || [];
      for (var i = 0; i < brands.length; i++) {
        if (/headless/i.test(brands[i].brand || '')) { s.hard.push('headless-brand'); break; }
      }
      if (/headless/i.test(navigator.userAgent)) s.hard.push('headless-ua');

      // Zero outer dimensions: classic headless tell. Guard against
      // legitimate zero in some embedded webviews by pairing with UA check.
      if (window.outerWidth === 0 && window.outerHeight === 0) s.soft.push('zero-outer');

      // No languages at all
      if (!navigator.languages || navigator.languages.length === 0) s.soft.push('no-langs');

      // Implausible hardware
      if (navigator.hardwareConcurrency === 0) s.soft.push('zero-cores');

    } catch (e) {
      log('signal error', e);
    }

    return s;
  }

  // Compare UA-string Chrome major against Client Hints major.
  // A spoofed UA usually forgets to keep these in sync.
  function uaVersionMismatch() {
    try {
      if (!navigator.userAgentData || !navigator.userAgentData.brands) return false;
      var m = navigator.userAgent.match(/Chrome\/(\d+)/);
      if (!m) return false;
      var uaMajor = parseInt(m[1], 10);

      var chBrand = navigator.userAgentData.brands.find(function (b) {
        return /Chromium|Google Chrome/i.test(b.brand);
      });
      if (!chBrand) return false;
      var chMajor = parseInt(String(chBrand.version).split('.')[0], 10);
      if (!uaMajor || !chMajor) return false;

      return Math.abs(uaMajor - chMajor) >= 2;
    } catch (e) {
      return false;
    }
  }

  var signals = collectSignals();
  if (uaVersionMismatch()) signals.hard.push('ua-mismatch');

  // Geo suppression: keep Singapore sessions out of analytics entirely.
  // Interaction must never release a geo-suppressed session.
  var geoSuppressed = (function () {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Singapore'; }
    catch (e) { return false; }
  })();
  if (geoSuppressed) signals.hard.push('geo-suppressed');

  var isSuspect = signals.hard.length > 0 || signals.soft.length >= 2;
  log('signals', signals, 'suspect:', isSuspect);

  // Clean visitor: do nothing at all. No hooks, no overhead.
  if (!isSuspect) { log('clean, no gating'); return; }

  // ---- quarantine ------------------------------------------------------

  var released = false;
  var heldScripts = [];   // deferred <script> elements
  var heldCalls = [];     // queued network callbacks
  var origFetch = window.fetch;
  var origBeacon = navigator.sendBeacon && navigator.sendBeacon.bind(navigator);

  function matches(url, list) {
    if (!url) return false;
    var u = String(url).toLowerCase();
    for (var i = 0; i < list.length; i++) {
      if (u.indexOf(list[i].toLowerCase()) !== -1) return true;
    }
    return false;
  }

  function shouldHoldScript(el) {
    var src = el.src || '';
    var txt = el.textContent || '';
    return matches(src, CONFIG.suppress) ||
           CONFIG.suppress.some(function (m) { return txt.indexOf(m) !== -1; });
  }

  // -- script deferral via MutationObserver
  var observer;
  try {
    observer = new MutationObserver(function (muts) {
      if (released) return;
      try {
        for (var i = 0; i < muts.length; i++) {
          var nodes = muts[i].addedNodes;
          for (var j = 0; j < nodes.length; j++) {
            var n = nodes[j];
            if (!n || n.nodeType !== 1 || n.tagName !== 'SCRIPT') continue;
            if (!shouldHoldScript(n)) continue;
            heldScripts.push({ src: n.src, text: n.textContent });
            n.type = 'text/plain';   // prevents execution
            log('held script', n.src || '(inline)');
          }
        }
      } catch (e) { log('observer error', e); release('observer-error'); }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {
    log('observer setup failed', e);
    return; // fail open
  }

  // -- network deferral
  try {
    window.fetch = function () {
      var args = arguments;
      var url = args[0] && (args[0].url || args[0]);
      if (released || !matches(url, CONFIG.suppressNet)) {
        return origFetch.apply(window, args);
      }
      log('held fetch', url);
      return new Promise(function (resolve) {
        heldCalls.push(function () {
          origFetch.apply(window, args).then(resolve, resolve);
        });
      });
    };

    if (origBeacon) {
      navigator.sendBeacon = function (url, data) {
        if (released || !matches(url, CONFIG.suppressNet)) {
          return origBeacon(url, data);
        }
        log('held beacon', url);
        heldCalls.push(function () { try { origBeacon(url, data); } catch (e) {} });
        return true; // caller believes it succeeded
      };
    }
  } catch (e) {
    log('net hook failed', e);
    release('net-hook-error');
  }

  // ---- release ---------------------------------------------------------

  function release(reason) {
    if (released) return;
    released = true;
    log('RELEASE:', reason);

    try { if (observer) observer.disconnect(); } catch (e) {}
    try { window.fetch = origFetch; } catch (e) {}
    try { if (origBeacon) navigator.sendBeacon = origBeacon; } catch (e) {}

    // Re-create deferred scripts in original order.
    try {
      heldScripts.forEach(function (h) {
        var s = document.createElement('script');
        if (h.src) { s.src = h.src; s.async = true; }
        else { s.textContent = h.text; }
        (document.head || document.documentElement).appendChild(s);
      });
      heldScripts.length = 0;
    } catch (e) { log('script replay error', e); }

    // Flush queued network calls.
    try {
      var calls = heldCalls.splice(0);
      calls.forEach(function (fn) { try { fn(); } catch (e) {} });
    } catch (e) { log('net replay error', e); }

    cleanupListeners();
  }

  // Permanently withhold. Bot session never enters analytics.
  function withhold(reason) {
    if (released) return;
    released = true; // stop further queuing; nothing is replayed
    log('WITHHOLD:', reason);
    try { if (observer) observer.disconnect(); } catch (e) {}
    try { window.fetch = origFetch; } catch (e) {}
    try { if (origBeacon) navigator.sendBeacon = origBeacon; } catch (e) {}
    heldScripts.length = 0;
    heldCalls.length = 0;
    cleanupListeners();
  }

  // ---- release triggers ------------------------------------------------

  var EVENTS = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'scroll'];

  function onInteract(e) {
    if (released) return;
    if (geoSuppressed) return;
    if (!e || !e.isTrusted) return;
    if (document.hidden) return;
    try {
      if (navigator.userActivation && !navigator.userActivation.hasBeenActive) {
        // pointermove alone doesn't set userActivation; allow it through
        if (e.type !== 'pointermove' && e.type !== 'scroll') return;
      }
    } catch (e2) {}
    release('interaction:' + e.type);
  }

  function cleanupListeners() {
    EVENTS.forEach(function (t) {
      try { window.removeEventListener(t, onInteract); } catch (e) {}
    });
  }

  EVENTS.forEach(function (t) {
    window.addEventListener(t, onInteract, { passive: true, capture: true });
  });

  // Dwell path: keeps bounce traffic. Only for soft-signal sessions;
  // hard signals (webdriver, playwright) never release on dwell alone.
  if (signals.hard.length === 0) {
    setTimeout(function () {
      if (released) return;
      if (document.hidden) return;          // background tab: not a real view
      if (document.visibilityState !== 'visible') return;
      release('dwell');
    }, CONFIG.dwellReleaseMs);
  }

  // Safety valve: hard signals hold for maxHoldMs then withhold permanently.
  setTimeout(function () {
    if (released) return;
    if (signals.hard.length > 0) withhold('hard-signal-timeout');
    else release('max-hold');
  }, CONFIG.maxHoldMs);

  // If the page unloads before release, the session simply never counted.
})();
